import React, { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { alpha } from '@mui/material';

// HOOKS
import { FeatureId, isFeatureEnabledForCurrentTenant } from '@warehouse/shared/config';
import {
	TitleEditorStoreEffects,
	TitleEditorStoreProvider,
	titleEditorStoreSelector,
	usePoster,
	useTitle,
	useTitleEditorStore,
} from '@warehouse/title/domain';
import { CenteredNxLoader, TabPanel, TabsHeader, TabsWrapper } from '@warehouse/shared/ui';
import { EditPreventer } from '@warehouse/title/feature-edit-preventer-popup';
import { externalIntegrationsRepositorySingleton } from '@warehouse/object-browser/infra';
import { useStateQuery } from '@warehouse/shared/util';
import { useSearchRelationshipChildren } from '../../../libs/title/domain/useSearchRelationshipChildren';

// LIBRARY
import TitleOverviewLoader from '../../components/titles/TitleOverview/TitleOverview';
import { useURLFragment } from '../../utils/hooks/useURLFragment';
import { ProductMetadataTab } from './tab/productMetadata';
import TitleSettings from './TitleSettings/TitleSettings';
import { Relationships } from './tab/relationships';
import ActivityTab from './tab/activity';
import ArtworkTab from './tab/artwork';
import { NxTensionTab } from './tab/NxTension';
import TracksTab from './tab/tracks/TracksTab';
import OrdersTab from './tab/orders/OrdersTab';
import UnsavedChangesContextProvider, { UnsavedChangesContext } from './contexts/UnsavedChangesContext';
import { ChildrenTab } from './tab/children-tab';
import { useChildrenTabs } from './hooks/useChildrenTabs';
import { useRequiredParam } from '../../../libs/shared/util/use-required-params';
import TitleAutoSaveQueueContextProvider from './contexts/TitleAutoSaveQueueContext';
import TitleAutoSaveContextProvider from './contexts/TitleAutoSaveContext';
import { ObjectsTab } from './tab/objects/ObjectsTab';
import { NxOrdersTab } from './tab/NxOrders';
import { NxInventoryTab } from './tab/NxInventory';

const Wrapper = styled.div(
	({ theme }) => css`
		display: flex;
		flex-direction: column;
		gap: ${theme.spacing(2)};
		overflow-y: auto;
		padding: ${theme.spacing(2)};
		width: 100%;
	`,
);

const BackdropWrapper = styled.div<{ url?: string }>(
	({ url }) => css`
		background-color: #dde0e3;
		${url &&
		css`
			background-image: url('${url}');
		`}
		background-position: center;
		background-size: cover;
		width: 100%;
	`,
);

const BackdropContainer = styled.div(
	() => css`
		display: flex;
		height: 100%;
		position: relative;
		z-index: 0;

		&::before {
			backdrop-filter: blur(8px);
			content: '';
			inset: 0;
			pointer-events: none;
			position: absolute;
			z-index: -1;
		}
	`,
);

const GlobalStyleTitleOverviewShadow = createGlobalStyle(
	() => css`
		.title-overview-shadow {
			border-radius: 8px;
			box-shadow: 0 30px 40px ${alpha('#000000', 0.5)};
		}
	`,
);

const TitleOverviewWrapper = styled.div<{ showBoxShadow?: boolean }>(
	() => css`
		flex: 0 0 auto;
		position: sticky;
		top: 0;
		z-index: 20; // Needed to be above the SimpleTable
	`,
);

export function AdminTitleView() {
	return (
		<TitleEditorStoreProvider>
			<TitleEditorStoreEffects />
			<EditPreventer>
				<TitleAutoSaveQueueContextProvider>
					<TitleAutoSaveContextProvider>
						<UnsavedChangesContextProvider>
							<AdminTitleViewLoader />
						</UnsavedChangesContextProvider>
					</TitleAutoSaveContextProvider>
				</TitleAutoSaveQueueContextProvider>
			</EditPreventer>
		</TitleEditorStoreProvider>
	);
}

function AdminTitleViewLoader() {
	const id = useRequiredParam('id');

	const { setTitleUuid } = useTitleEditorStore(titleEditorStoreSelector.actions);

	useEffect(() => {
		setTitleUuid(id);
	}, [id, setTitleUuid]);

	const isTitlePending = useTitleEditorStore(titleEditorStoreSelector.isPending);

	if (isTitlePending) return <CenteredNxLoader />;

	// This is a workaround to make sure the whole page and its components are re-rendered when the title changes
	return <AdminTitleViewComp key={id} titleId={id} />;
}

export function AdminTitleViewComp({ titleId }: { titleId: string }) {
	// state
	const { promptUserIfUnsavedChanges } = useContext(UnsavedChangesContext);
	const [fragment, setFragment] = useURLFragment();
	const [settingsTab, setSettingsTab] = useStateQuery<string>({
		queryName: 'settings',
		type: 'string',
	});
	const [selectedTab, setSelectedTab] = useState(-1);
	const titleOverviewWrapperRef = useRef<HTMLDivElement>(null);
	// hooks
	const { data: posterData } = usePoster();
	const title = useTitle({ uuid: titleId });
	const loading = !title.data;

	const childrenTabs = useChildrenTabs(title.data);
	const relationships = useSearchRelationshipChildren(titleId);

	// ref
	const wrapperRef = useRef<HTMLDivElement>(null);

	// effects
	const tabs = useMemo<TabDefinition[]>(
		() =>
			title.data
				? [
						...childrenTabs.map((tab) => ({
							defaultUrlState: tab.pluralLabel.toLowerCase(),
							label: tab.pluralLabel,
							component: <ChildrenTab tabProperties={tab} title={title.data!} />,
						})),
						{
							defaultUrlState: 'product-metadata/localized-info/default',
							label: 'Product Metadata',
							component: <ProductMetadataTab title={title.data} wrapperRef={wrapperRef} />,
						},
						...addTabIfVisible(
							{
								defaultUrlState: 'objects',
								label: 'Objects',
								scrollMode: 'content',
								component: <ObjectsTab wrapperRef={wrapperRef} />,
							},
							'title-objects-tab',
						),
						...addTabIfVisible(
							{
								defaultUrlState: 'tracks',
								label: 'Tracks',
								component: <TracksTab wrapperRef={wrapperRef} />,
							},
							'title-tracks-tab',
						),
						...addTabIfVisible(
							{
								defaultUrlState: 'nxInventory',
								label: externalIntegrationsRepositorySingleton.get().getExternalTab('nxInventory')?.name ?? 'Inventory',
								component: <NxInventoryTab />,
								disabled: false,
							},
							'title-nx-inventory-tab',
						),
						...addTabIfVisible(
							{
								defaultUrlState: 'artwork',
								label: 'Artwork',
								component: <ArtworkTab />,
								disabled: false,
							},
							'title-artworks-tab',
						),
						...addTabIfVisible(
							{
								defaultUrlState: 'orders',
								label: 'Orders',
								component: <OrdersTab />,
							},
							'title-orders-tab',
						),
						...addTabIfVisible(
							{
								defaultUrlState: 'nxorders',
								label: externalIntegrationsRepositorySingleton.get().getExternalTab('nxOrders')?.name ?? 'Orders',
								component: <NxOrdersTab />,
								disabled: false,
							},
							'title-nx-orders-tab',
						),
						{
							defaultUrlState: 'relationships',
							label: 'Relationships',
							component: <Relationships />,
							disabled: false,
						},
						...addTabIfVisible(
							{
								defaultUrlState: 'nxtension',
								label: externalIntegrationsRepositorySingleton.get().getExternalTab('nxTension')?.name ?? 'NxTension',
								component: <NxTensionTab />,
								disabled: false,
							},
							'title-nx-tension-tab',
						),
						...addTabIfVisible(
							{
								defaultUrlState: 'activity/comments',
								label: 'Activity',
								component: <ActivityTab />,
								disabled: false,
							},
							'title-activity-tabs',
						),
					]
				: [],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[title.data, childrenTabs],
	);

	// Put shadow on title overview when scrolling
	useEffect(() => {
		const handleScroll = () => {
			const isScrolled = (wrapperRef.current && wrapperRef.current.scrollTop > 0) || false;
			if (titleOverviewWrapperRef.current) {
				if (isScrolled) {
					titleOverviewWrapperRef.current.classList.add('title-overview-shadow');
				} else {
					titleOverviewWrapperRef.current.classList.remove('title-overview-shadow');
				}
			}
		};

		wrapperRef.current?.addEventListener('scroll', handleScroll);

		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			wrapperRef.current?.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const goToDefaultTab = () => {
		if (childrenTabs.length) setFragment(childrenTabs[0].pluralLabel.toLowerCase());
		else if (isFeatureEnabledForCurrentTenant('title-orders-tab')) setFragment('orders');
		else setFragment('product-metadata/localized-info/default');
	};

	useEffect(() => {
		if (loading || !relationships.data || !tabs.length) return;

		if (!fragment) {
			goToDefaultTab();
			return;
		}
		const requestedTabName = fragment.split('/')[0];
		const tabIndex = tabs.findIndex((tab) => tab.defaultUrlState.startsWith(requestedTabName));

		if (tabIndex >= 0) {
			setSelectedTab(tabIndex);
			return;
		}

		goToDefaultTab();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fragment, loading, relationships.data, tabs]);

	if (loading || selectedTab < 0) {
		return <CenteredNxLoader />;
	}

	return (
		<>
			<BackdropWrapper url={posterData?.backdrop || posterData?.poster}>
				<BackdropContainer ref={wrapperRef}>
					<GlobalStyleTitleOverviewShadow />
					<Wrapper>
						<TitleOverviewWrapper ref={titleOverviewWrapperRef}>
							<TitleOverviewLoader onClickSettings={() => setSettingsTab('general')} titleFull={title.data} />
						</TitleOverviewWrapper>
						<TabsWrapper $scrollMode={tabs[selectedTab]?.scrollMode || 'page'}>
							<TabsHeader
								selectedTab={selectedTab}
								onChange={(newSelectedTab) => {
									promptUserIfUnsavedChanges(() =>
										setFragment(tabs[newSelectedTab]?.defaultUrlState, { basePathname: `/title/${titleId}` }),
									);
								}}
								tabs={tabs}
								prefix="simple"
							/>
							{tabs.map((tab, index) => (
								<TabPanel prefix="simple" scrollMode={tab.scrollMode} key={index} value={selectedTab} index={index}>
									{tab.component}
								</TabPanel>
							))}
						</TabsWrapper>
					</Wrapper>
				</BackdropContainer>
			</BackdropWrapper>
			<TitleSettings
				wrapperRef={wrapperRef}
				open={!!settingsTab}
				selectedTabQuery={settingsTab}
				onSettingsTabChange={setSettingsTab}
				data={title.data}
				handleClose={() => setSettingsTab(null)}
			/>
		</>
	);
}

type TabDefinition = {
	defaultUrlState: string;
	label: string;
	component: ReactNode;
	scrollMode?: 'page' | 'content';
	disabled?: boolean;
};

function addTabIfVisible(tab: TabDefinition, elementId: FeatureId): TabDefinition[] {
	return isFeatureEnabledForCurrentTenant(elementId) ? [tab] : [];
}
